import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { MonthViewProps } from './MonthView.types';
import CalendarDateCell from '../CalendarDateCell';
import { CalendarDateInfo } from '../../../types/dentist/calendar';
import { getDateDisplayInfo, isPastDate } from '../../../utils/dentist/calendar/jobProcessing';
import styles from './MonthView.module.scss';

const MonthView: React.FC<MonthViewProps> = ({ 
  currentDate, 
  onDateClick, 
  onMonthChange,
  dateJobsMap = {},
  getDisplayInfo
}) => {
  const monthStart = dayjs(currentDate).startOf('month');
  const monthEnd = dayjs(currentDate).endOf('month');
  const startDate = monthStart.startOf('week');
  const endDate = monthEnd.endOf('week');

  const handlePrevMonth = () => {
    const newDate = dayjs(currentDate).subtract(1, 'month').toDate();
    onMonthChange(newDate);
  };

  const handleNextMonth = () => {
    const newDate = dayjs(currentDate).add(1, 'month').toDate();
    onMonthChange(newDate);
  };

  // Generate calendar dates with job info
  const calendarDates = useMemo(() => {
    const dates: CalendarDateInfo[] = [];
    let day = startDate;

    while (day.isBefore(endDate) || day.isSame(endDate, 'day')) {
      const dateKey = day.format('YYYY-MM-DD');
      const jobs = dateJobsMap[dateKey] || [];
      const dayDate = day.toDate();
      const displayInfo = getDisplayInfo
        ? getDisplayInfo(dayDate, jobs)
        : getDateDisplayInfo(dayDate, jobs);
      
      dates.push({
        date: dayDate,
        dateKey,
        isCurrentMonth: day.isSame(monthStart, 'month'),
        jobs,
        ...displayInfo
      });
      
      day = day.add(1, 'day');
    }

    return dates;
  }, [startDate, endDate, monthStart, dateJobsMap, getDisplayInfo]);

  // Week days header
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={styles.monthView}>
      <div className={styles.header}>
        <button 
          className={styles.navButton}
          onClick={handlePrevMonth}
          aria-label="Previous month"
        >
          ‹
        </button>
        <h2 className={styles.monthTitle}>
          {monthStart.format('MMMM YYYY')}
        </h2>
        <button 
          className={styles.navButton}
          onClick={handleNextMonth}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className={styles.weekDays}>
        {weekDays.map(day => (
          <div key={day} className={styles.weekDay}>
            {day}
          </div>
        ))}
      </div>

      <div className={styles.dates}>
        {calendarDates.map((dateInfo, index) => (
          <CalendarDateCell
            key={`${dateInfo.dateKey}-${index}`}
            dateInfo={dateInfo}
            onClick={onDateClick}
          />
        ))}
      </div>
    </div>
  );
};

export default MonthView;
